import React, { useState, useEffect, useContext } from 'react';
import { axiosInstance } from '../../Services/axios';
import { BRAIN_SNAP_PROCESS, BRAIN_SNAP_JOB, BRAIN_SNAP_CLARIFY, BRAIN_SNAP_HISTORY, BRAIN_SNAP_HISTORY_DETAIL, BRAIN_SNAP_DELETE, BRAIN_SNAP_DOWNLOAD, STRIPE_USAGE_INFO } from '../../Services/endpoints';
import { AiOutlineDownload } from 'react-icons/ai';
import { MdDelete, MdShare } from 'react-icons/md';
import PaymentModal from '../../components/PaymentModal/PaymentModal';
import { AppContext } from '../../store/AppContext';
import './brainSnap.scss';

const BrainSnap = () => {
    const { setUsageInfo: setContextUsageInfo } = useContext(AppContext);
    // Tab state
    const [activeTab, setActiveTab] = useState('generate');

    // Generation states
    const [abstract, setAbstract] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [processingStage, setProcessingStage] = useState('');

    // History states
    const [historyImages, setHistoryImages] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailedImage, setDetailedImage] = useState(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [fadingOutImageId, setFadingOutImageId] = useState(null);

    // Delete confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteCandidateId, setDeleteCandidateId] = useState(null);

    // Payment states
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [usageInfo, setUsageInfo] = useState(null);
    const [paymentVerifying, setPaymentVerifying] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);

    // Clarification states
    const [questionsAsked, setQuestionsAsked] = useState(0);
    const [clarificationQuestion, setClarificationQuestion] = useState('');
    const [clarificationAnswer, setClarificationAnswer] = useState('');
    const [askedQuestion, setAskedQuestion] = useState('');
    const [isLoadingClarification, setIsLoadingClarification] = useState(false);
    const [clarificationLoadingStage, setClarificationLoadingStage] = useState('');
    const [clarificationError, setClarificationError] = useState(null);

    const MAX_QUESTIONS = 3;

    // Fetch usage info on mount
    useEffect(() => {
        fetchUsageInfo();

        // Check if returning from Stripe payment
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const canceled = urlParams.get('canceled');

        if (sessionId) {
            console.log('[BRAINSNAP] Detected session_id from Stripe payment:', sessionId);
            verifyPaymentSession(sessionId);
        } else if (canceled) {
            console.log('[BRAINSNAP] Payment was canceled');
        }
    }, []);

    // Function to fetch generation usage info
    const fetchUsageInfo = async () => {
        try {
            const response = await axiosInstance.get(STRIPE_USAGE_INFO);
            if (response.data.success) {
                setUsageInfo(response.data.data);
                setContextUsageInfo(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching usage info:', err);
        }
    };

    // Function to verify payment session after returning from Stripe
    const verifyPaymentSession = async (sessionId) => {
        setPaymentVerifying(true);
        try {
            console.log('[BRAINSNAP] Verifying payment session:', sessionId);
            const response = await axiosInstance.get(`/brain-snap/stripe/session-status/${sessionId}`);

            console.log('[BRAINSNAP] Payment verification response:', response.data);

            if (response.data.success && response.data.status === 'completed') {
                console.log('[BRAINSNAP] Payment completed successfully!');
                setPaymentSuccess(true);

                // Refresh usage info to show updated generation count
                console.log('[BRAINSNAP] Refreshing usage info...');
                await fetchUsageInfo();

                // Clean up URL
                window.history.replaceState({}, document.title, window.location.pathname);

                // Show success message for 3 seconds then hide
                setTimeout(() => {
                    setPaymentSuccess(false);
                }, 3000);
            } else {
                console.log('[BRAINSNAP] Payment verification returned non-completed status:', response.data.status);
            }
        } catch (err) {
            console.error('[BRAINSNAP] Error verifying payment:', err);
        } finally {
            setPaymentVerifying(false);
        }
    };

    // Helper function to parse markdown formatting in answer text
    const parseMarkdownText = (text) => {
        // Handle both **text** and *text* markdown formatting
        const regex = /(\*\*.*?\*\*|\*[^*\s]+\*)/g;
        const parts = text.split(regex);

        return parts.map((part, index) => {
            if (!part) return null;
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={index}>{part.slice(2, -2)}</strong>;
            } else if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
                return <strong key={index}>{part.slice(1, -1)}</strong>;
            }
            return part;
        }).filter(item => item !== null);
    };

    // Generation runs as a background job on the server; the client polls until
    // it finishes rather than holding a request open for minutes.
    const JOB_POLL_INTERVAL_MS = 3000;
    const JOB_POLL_CEILING_MS = 6 * 60 * 1000;

    const pollForJobResult = async (jobId) => {
        const deadline = Date.now() + JOB_POLL_CEILING_MS;

        while (Date.now() < deadline) {
            await new Promise((resolve) => setTimeout(resolve, JOB_POLL_INTERVAL_MS));

            let response;
            try {
                response = await axiosInstance.get(BRAIN_SNAP_JOB(jobId));
            } catch (pollError) {
                // A single failed poll (blip, sleeping laptop) should not kill a
                // job that is still running - keep trying until the ceiling
                if (pollError.response?.status === 404) {
                    throw new Error('Generation job not found. Please try again.');
                }
                continue;
            }

            const { status, data, error: jobError } = response.data || {};

            if (status === 'COMPLETED') {
                return { data };
            }
            if (status === 'FAILED') {
                throw new Error(jobError || 'Generation failed. Please try again.');
            }
        }

        throw new Error(
            'This is taking longer than expected. Your figure may still be generating - check the History tab in a few minutes.'
        );
    };

    const handleProcess = async () => {
        if (!abstract.trim()) {
            setError('Please enter an abstract');
            return;
        }

        // Check if user can generate
        if (usageInfo && !usageInfo.canGenerate) {
            setShowPaymentModal(true);
            return;
        }

        setIsProcessing(true);
        setError(null);
        setResults(null);
        setProgress(0);
        setProcessingStage('Analyzing your research abstract...');

        // Simulate progress updates with stage transitions - tuned for ~90s duration
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                // Slower increment: avg 1% per second = ~90-100s to reach 100%
                // Cap at 95% until actual response comes back
                if (prev >= 95) return prev;

                const increment = Math.random() * 1.0 + 0.5; // 0.5% to 1.5% increment
                const newProgress = prev + increment;

                // Update stage based on progress - evenly spaced
                if (newProgress < 25) {
                    setProcessingStage('Analyzing your research abstract...');
                } else if (newProgress < 50) {
                    setProcessingStage('Extracting entities and relationships...');
                } else if (newProgress < 75) {
                    setProcessingStage('Generating your graphical abstract...');
                } else {
                    setProcessingStage('Just a few more minutes...');
                }

                return newProgress;
            });
        }, 1000);

        try {
            // The server queues the job and returns immediately - generation
            // takes minutes, which no proxy will hold a connection open for
            const queued = await axiosInstance.post(BRAIN_SNAP_PROCESS, {
                abstract: abstract.trim(),
                title: 'Research Figure'
            });

            let body = queued?.data;
            // Mis-routed SPA HTML (200) still lands here as a string - treat clearly
            if (typeof body === 'string') {
                const trimmed = body.trim();
                if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')) {
                    throw new Error(
                        'Could not reach the generation service. Please hard-refresh the page and try again.'
                    );
                }
                try {
                    body = JSON.parse(trimmed);
                } catch {
                    throw new Error('Unexpected response from the generation service. Please try again.');
                }
            }

            const jobId =
                body?.jobId ||
                body?.imageId ||
                body?.id ||
                body?.data?.jobId ||
                null;

            if (!jobId) {
                console.error('Process response missing job id:', body);
                throw new Error(
                    'Server did not return a job id. If this continues, log out and log back in, then try again.'
                );
            }

            const result = await pollForJobResult(jobId);

            clearInterval(progressInterval);
            setProgress(100);

            if (result?.data) {
                setResults(result.data);
                // Refresh usage info after successful generation
                fetchUsageInfo();
            } else {
                setError('No results returned from server');
            }
        } catch (err) {
            clearInterval(progressInterval);
            setProgress(0);
            console.error('BrainSnap processing error:', err);

            // Check for payment required error (402)
            if (err.response?.status === 402 || err.response?.data?.code === 'PAYMENT_REQUIRED') {
                setShowPaymentModal(true);
                setError('No generations remaining. Please purchase more to continue.');
            } else if (
                err.response?.status === 401 ||
                err.response?.status === 403 ||
                err.response?.data?.message === 'TokenExpired' ||
                err.response?.data?.message === 'Authentication error' ||
                err.response?.data?.message === 'Access token is missing'
            ) {
                setError('Your session has expired. Please log in again, then generate.');
            } else {
                // Prefer string fields only - API often sends error: true (boolean)
                const d = err.response?.data;
                const errorMessage =
                    (typeof d?.message === 'string' && d.message) ||
                    (typeof d?.error === 'string' && d.error) ||
                    (typeof d?.details === 'string' && d.details) ||
                    err.message ||
                    'Failed to process abstract. Please try again.';
                setError(errorMessage);
            }
        } finally {
            setTimeout(() => {
                setIsProcessing(false);
                setProgress(0);
                setProcessingStage('');
            }, 500);
        }
    };

    const handleClear = () => {
        setAbstract('');
        setResults(null);
        setError(null);
        setQuestionsAsked(0);
        setClarificationQuestion('');
        setClarificationAnswer('');
        setAskedQuestion('');
        setClarificationError(null);
    };

    /** Switch to the generate form (e.g. from History). Do not kill an in-flight job. */
    const handleNewGenerationTab = () => {
        setActiveTab('generate');
        setShowDetailModal(false);
        setSelectedImage(null);
        setDetailedImage(null);
        setShowDeleteConfirm(false);
        if (isProcessing) {
            return;
        }
        // Drop completed results so the Abstract input shows again
        setResults(null);
        setError(null);
        setProgress(0);
        setProcessingStage('');
        setQuestionsAsked(0);
        setClarificationQuestion('');
        setClarificationAnswer('');
        setAskedQuestion('');
        setClarificationError(null);
    };

    const handleDownloadImage = () => {
        if (results?.generated_image?.image_base64) {
            const link = document.createElement('a');
            link.href = `data:image/jpeg;base64,${results.generated_image.image_base64}`;
            link.download = `brainsnap_figure_${Date.now()}.jpg`;
            link.click();
        }
    };

    const handleAskQuestion = async () => {
        if (!clarificationQuestion.trim()) {
            setClarificationError('Please enter a question');
            return;
        }

        if (questionsAsked >= MAX_QUESTIONS) {
            setClarificationError('You have reached the maximum number of questions for this analysis.');
            return;
        }

        setIsLoadingClarification(true);
        setClarificationError(null);
        setClarificationAnswer('');
        setClarificationLoadingStage('Analyzing your question...');

        // Simulate loading stages with stage transitions
        const loadingStages = ['Analyzing your question...', 'Almost there...'];
        let stageIndex = 0;

        const loadingInterval = setInterval(() => {
            stageIndex++;
            if (stageIndex < loadingStages.length) {
                setClarificationLoadingStage(loadingStages[stageIndex]);
            } else {
                clearInterval(loadingInterval);
            }
        }, 1000);

        try {
            const response = await axiosInstance.post(BRAIN_SNAP_CLARIFY, {
                question: clarificationQuestion.trim(),
                abstract: results.abstract,
                entities: results.entities,
                relationships: results.relationships,
                research_figure_prompt: results.research_figure_prompt
            });

            clearInterval(loadingInterval);

            if (response.data.success && response.data.answer) {
                setAskedQuestion(clarificationQuestion.trim());
                setClarificationAnswer(response.data.answer);
                setQuestionsAsked(questionsAsked + 1);
                setClarificationQuestion('');
            } else {
                setClarificationError('Failed to get clarification. Please try again.');
            }
        } catch (err) {
            clearInterval(loadingInterval);
            console.error('Clarification error:', err);
            const errorMessage = err.response?.data?.error
                || err.response?.data?.details
                || err.message
                || 'Failed to get clarification. Please try again.';
            setClarificationError(errorMessage);
        } finally {
            setIsLoadingClarification(false);
            setClarificationLoadingStage('');
        }
    };

    // Fetch history images
    const fetchHistory = async () => {
        setIsLoadingHistory(true);
        setHistoryError(null);

        try {
            const response = await axiosInstance.get(BRAIN_SNAP_HISTORY, {
                params: { limit: 20 }
            });

            if (response.data.success) {
                setHistoryImages(response.data.images || []);
            } else {
                setHistoryError('Failed to load history');
            }
        } catch (err) {
            console.error('Error fetching history:', err);
            const errorMessage = err.response?.data?.error
                || err.message
                || 'Failed to load history. Please try again.';
            setHistoryError(errorMessage);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Fetch detailed image data
    const fetchImageDetail = async (imageId) => {
        setIsLoadingDetail(true);
        try {
            const response = await axiosInstance.get(BRAIN_SNAP_HISTORY_DETAIL(imageId));

            if (response.data.success) {
                setDetailedImage(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching image detail:', err);
            alert('Failed to load image details. Please try again.');
            setShowDetailModal(false);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    // Handle opening detail modal
    const handleOpenDetailModal = (image) => {
        setSelectedImage(image);
        setShowDetailModal(true);
        fetchImageDetail(image.imageId);
    };

    // Handle closing detail modal
    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedImage(null);
        setDetailedImage(null);
    };

    const handleDownloadFromUrl = async (imageId, title) => {
        setIsDownloading(true);
        try {
            const response = await axiosInstance.get(BRAIN_SNAP_DOWNLOAD(imageId), {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `brainsnap_${title.replace(/\s+/g, '_')}_${Date.now()}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Give browser time to process download before revoking
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                setIsDownloading(false);
            }, 50);
        } catch (err) {
            console.error('Error downloading image:', err);
            alert('Unable to download image. Please try again.');
            setIsDownloading(false);
        }
    };

    const handleCopyToClipboard = async (imageId, title) => {
        setIsCopying(true);
        try {
            // Ensure focus is on the window before starting
            window.focus();

            const response = await axiosInstance.get(BRAIN_SNAP_DOWNLOAD(imageId), {
                responseType: 'blob'
            });

            // Create a blob from the JPEG and copy it directly
            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'image/jpeg': response.data
                    })
                ]);

                // If successful, reset after 700ms
                setTimeout(() => {
                    setIsCopying(false);
                }, 700);
            } catch (jpegErr) {
                // If JPEG fails, try converting to PNG
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const url = URL.createObjectURL(response.data);
                const img = new Image();

                img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);

                    canvas.toBlob((pngBlob) => {
                        window.focus();
                        navigator.clipboard.write([
                            new ClipboardItem({
                                'image/png': pngBlob
                            })
                        ]).then(() => {
                            setTimeout(() => {
                                setIsCopying(false);
                            }, 700);
                        }).catch((err) => {
                            console.error('PNG clipboard write failed:', err);
                            // Show copied anyway since user clicked the button
                            setTimeout(() => {
                                setIsCopying(false);
                            }, 700);
                        });

                        URL.revokeObjectURL(url);
                    }, 'image/png');
                };

                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    setTimeout(() => {
                        setIsCopying(false);
                    }, 700);
                };

                img.src = url;
            }
        } catch (err) {
            console.error('Error copying image to clipboard:', err);
            // Still show the animation even if there's an error
            setTimeout(() => {
                setIsCopying(false);
            }, 700);
        }
    };

    // Handle delete click - open confirmation
    const handleDelete = (e, imageId) => {
        e.stopPropagation();
        setDeleteCandidateId(imageId);
        setShowDeleteConfirm(true);
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setDeleteCandidateId(null);
    };

    const confirmDelete = async () => {
        if (!deleteCandidateId) return;

        setIsDeleting(true);
        try {
            const response = await axiosInstance.delete(BRAIN_SNAP_DELETE(deleteCandidateId));
            if (response.data.success) {
                // Start fade-out animation
                setFadingOutImageId(deleteCandidateId);
                setShowDeleteConfirm(false);

                // Wait for animation to complete (0.4s) then remove from state
                setTimeout(() => {
                    setHistoryImages(prev =>
                        prev.filter(img => img.imageId !== deleteCandidateId)
                    );
                    setFadingOutImageId(null);
                    setDeleteCandidateId(null);

                    // Reload history in background to sync with server
                    fetchHistory();
                }, 400);
            }
        } catch (err) {
            console.error('Error deleting image:', err);
            alert('Failed to delete image. Please try again.');
            setShowDeleteConfirm(false);
        } finally {
            setIsDeleting(false);
        }
    };

    // Load history when History tab is clicked
    useEffect(() => {
        if (activeTab === 'history') {
            fetchHistory();
        }
    }, [activeTab]);

    return (
        <div className="brain-snap-container">
            <div className="dot-background-container">
                <div className="dot-pattern"></div>
                <div className="dot-mask"></div>
            </div>
            <div className="brain-snap-header">
                <h1>BrainSNAP</h1>
                <p className="subtitle">AI-Powered Graphical Abstract Generator</p>
            </div>

            {/* Tab Navigation */}
            <div className="tab-navigation">
                <button
                    type="button"
                    className={`tab-button ${activeTab === 'generate' ? 'active' : ''}`}
                    onClick={handleNewGenerationTab}
                >
                    New Generation
                </button>
                <button
                    type="button"
                    className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveTab('history')}
                >
                    History {historyImages.length > 0 && `(${historyImages.length})`}
                </button>
            </div>

            <div className="brain-snap-content">
                {activeTab === 'generate' ? (
                    // GENERATION TAB
                    <>
                        {!results ? (
                            // Input View
                            <div className="input-view">
                                <div className="input-section">
                                    <label htmlFor="abstract">Abstract</label>
                                    <textarea
                                        id="abstract"
                                        placeholder="Paste your research abstract here..."
                                        value={abstract}
                                        onChange={(e) => setAbstract(e.target.value)}
                                        className="abstract-textarea"
                                        rows="12"
                                    />
                                </div>

                                {isProcessing && (
                                    <div className="progress-container">
                                        <div className="progress-bar">
                                            <div
                                                className="progress-fill"
                                                style={{ width: `${progress}%` }}
                                            >
                                                <span className="progress-text">{Math.round(progress)}%</span>
                                            </div>
                                        </div>
                                        <p className="progress-label">{processingStage}</p>
                                    </div>
                                )}

                                {error && (
                                    <div className="error-message">
                                        <span>⚠️</span> {error}
                                    </div>
                                )}

                                <div className="button-group">
                                    {!isProcessing && (
                                        <button
                                            onClick={handleProcess}
                                            className="process-button"
                                        >
                                            Generate Research Figure
                                        </button>
                                    )}
                                    {abstract && !isProcessing && (
                                        <button
                                            onClick={handleClear}
                                            className="clear-button"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Results View
                            <div className="results-view">
                                <div className="results-header">
                                    <h2>Generated Results</h2>
                                    <button onClick={handleClear} className="new-analysis-button">
                                        New Analysis
                                    </button>
                                </div>

                                {/* Abstract Section */}
                                <div className="abstract-section">
                                    <h3>Abstract</h3>
                                    <div className="abstract-content">
                                        <p>{results.abstract}</p>
                                    </div>
                                </div>

                                {/* Metadata Section */}
                                {(results.metadata || results.entities || results.relationships) && (
                                    <div className="metadata-section">
                                        <h3>Analysis Summary</h3>
                                        <div className="metadata-grid">
                                            {results.metadata && (
                                                <div className="metadata-item">
                                                    <strong>Entities:</strong> {results.metadata.total_entities}
                                                </div>
                                            )}
                                            {results.metadata && (
                                                <div className="metadata-item">
                                                    <strong>Relationships:</strong> {results.metadata.total_relationships}
                                                </div>
                                            )}
                                            {results.metadata && (
                                                <div className="metadata-item">
                                                    <strong>Processing Time:</strong> {results.metadata.processing_time_seconds}s
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Generated Image Section */}
                                {results.generated_image && results.generated_image.success && results.generated_image.image_base64 && (
                                    <div className="generated-image-section">
                                        <h3>Generated Research Figure</h3>
                                        <div className="image-container">
                                            <div className="image-wrapper">
                                                <img
                                                    src={`data:image/jpeg;base64,${results.generated_image.image_base64}`}
                                                    alt="Generated Research Figure"
                                                    className="generated-image"
                                                />
                                                <button
                                                    className="download-overlay"
                                                    onClick={handleDownloadImage}
                                                    title="Download research figure"
                                                >
                                                    <AiOutlineDownload />
                                                    <span>Download</span>
                                                </button>
                                            </div>
                                            {results.generated_image.generation_time && (
                                                <div className="image-metadata">
                                                    <span>Image generated in {results.generated_image.generation_time}s</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Image Generation Error */}
                                {results.generated_image && !results.generated_image.success && results.generated_image.error && (
                                    <div className="image-error-section">
                                        <h3>Image Generation</h3>
                                        <p className="error-message">Failed to generate image: {results.generated_image.error}</p>
                                    </div>
                                )}

                                {/* Clarifications Section */}
                                <div className="clarifications-section">
                                    <h3>Questions About This Figure</h3>

                                    {/* Questions Counter */}
                                    <div className="questions-counter">
                                        <div className="counter-label">
                                            Questions Remaining: <strong>{MAX_QUESTIONS - questionsAsked} of {MAX_QUESTIONS}</strong>
                                        </div>
                                        <div className="progress-bar-small">
                                            <div
                                                className="progress-fill-small"
                                                style={{ width: `${(questionsAsked / MAX_QUESTIONS) * 100}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Question Input Area */}
                                    {questionsAsked < MAX_QUESTIONS ? (
                                        <div className="question-input-area">
                                            <div className="question-input-group">
                                                <textarea
                                                    value={clarificationQuestion}
                                                    onChange={(e) => setClarificationQuestion(e.target.value)}
                                                    placeholder="Ask a question about this research figure..."
                                                    className="clarification-input"
                                                    rows="3"
                                                    disabled={isLoadingClarification}
                                                />
                                                <button
                                                    onClick={handleAskQuestion}
                                                    className="ask-button"
                                                    disabled={isLoadingClarification || !clarificationQuestion.trim()}
                                                >
                                                    {isLoadingClarification ? 'Getting Answer...' : 'Ask Question'}
                                                </button>
                                            </div>

                                            {clarificationError && (
                                                <div className="clarification-error">
                                                    {clarificationError}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="limit-reached-message">
                                            <p>You have used all {MAX_QUESTIONS} questions for this analysis.</p>
                                            <p>Click "New Analysis" to generate a new figure and ask more questions.</p>
                                        </div>
                                    )}

                                    {/* Question and Answer Display */}
                                    {clarificationAnswer && (
                                        <div className="question-answer-container">
                                            <div className="clarification-question">
                                                <div className="question-header">Question:</div>
                                                <div className="question-content">
                                                    {askedQuestion}
                                                </div>
                                            </div>

                                            <div className="clarification-answer">
                                                <div className="answer-header">Answer:</div>
                                                <div className="answer-content">
                                                    {parseMarkdownText(clarificationAnswer)}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {isLoadingClarification && (
                                        <div className="loading-clarification">
                                            <div className="spinner" />
                                            <p>{clarificationLoadingStage}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    // HISTORY TAB
                    <div className="history-view">
                        <div className="history-header">
                            <h2>Your Generated Figures</h2>
                            <button onClick={fetchHistory} className="refresh-button">Refresh</button>
                        </div>

                        {isLoadingHistory && (
                            <div className="loading-history">
                                <svg className="animated-hourglass" width="73px" height="88px" viewBox="0 0 73 88" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                                    <g id="hourglass">
                                        <path d="M63.8761664,86 C63.9491436,84.74063 64,83.4707791 64,82.1818182 C64,65.2090455 57.5148507,50.6237818 48.20041,44 C57.5148507,37.3762182 64,22.7909545 64,5.81818182 C64,4.52922091 63.9491436,3.25937 63.8761664,2 L10.1238336,2 C10.0508564,3.25937 10,4.52922091 10,5.81818182 C10,22.7909545 16.4851493,37.3762182 25.79959,44 C16.4851493,50.6237818 10,65.2090455 10,82.1818182 C10,83.4707791 10.0508564,84.74063 10.1238336,86 L63.8761664,86 Z" id="glass" fill="#e5e7eb"></path>
                                        <rect id="top-plate" fill="#ec4899" x="0" y="0" width="74" height="8" rx="2"></rect>
                                        <rect id="bottom-plate" fill="#ec4899" x="0" y="80" width="74" height="8" rx="2"></rect>

                                        <g id="top-sand" transform="translate(18, 21)">
                                            <clipPath id="top-clip-path" fill="white">
                                                <rect x="0" y="0" width="38" height="21"></rect>
                                            </clipPath>

                                            <path fill="#ec4899" clipPath="url(#top-clip-path)" d="M38,0 C36.218769,7.51704545 24.818769,21 19,21 C13.418769,21 1.9,7.63636364 0,0 L38,0 Z"></path>
                                        </g>

                                        <g id="bottom-sand" transform="translate(18, 55)">
                                            <clipPath id="bottom-clip-path" fill="white">
                                                <rect x="0" y="0" width="38" height="21"></rect>
                                            </clipPath>

                                            <g clipPath="url(#bottom-clip-path)">
                                                <path fill="#ec4899" d="M0,21 L38,21 C36.1,13.3636364 24.581231,0 19,0 C13.181231,0 1.781231,13.4829545 0,21 Z"></path>
                                            </g>
                                        </g>
                                    </g>
                                </svg>
                                <p>Loading your history...</p>
                            </div>
                        )}

                        {historyError && (
                            <div className="error-message">
                                <span>⚠️</span> {historyError}
                            </div>
                        )}

                        {!isLoadingHistory && !historyError && historyImages.length === 0 && (
                            <div className="empty-history">
                                <p>No generated figures yet.</p>
                                <p>Create your first research figure to see it here!</p>
                                <button type="button" onClick={handleNewGenerationTab} className="generate-button">
                                    Generate Now
                                </button>
                            </div>
                        )}

                        {!isLoadingHistory && historyImages.length > 0 && (
                            <div className="history-gallery">
                                {historyImages.map((image) => (
                                    <div
                                        key={image.imageId}
                                        className={`image-card ${fadingOutImageId === image.imageId ? 'fade-out' : ''}`}
                                    >
                                        <div className="image-thumbnail">
                                            {image.thumbnail_url ? (
                                                <img
                                                    src={image.thumbnail_url}
                                                    alt={image.title || 'Research Figure'}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div className="image-placeholder" style={{ display: 'none' }}>
                                                <span>📊</span>
                                            </div>
                                        </div>

                                        <div className="image-info">
                                            <p className="abstract-preview">
                                                {image.abstract?.substring(0, 100)}...
                                            </p>
                                            <div className="metadata-row">
                                                <span> {image.metadata?.total_entities || 0} Entities</span>
                                                <span> {image.metadata?.total_relationships || 0} Relations</span>
                                            </div>
                                            <div className="date-row">
                                                {new Date(image.createdAt).toLocaleDateString()}
                                            </div>
                                        </div>

                                        <div className="image-actions">
                                            <button
                                                className="view-button"
                                                onClick={() => handleOpenDetailModal(image)}
                                            >
                                                View
                                            </button>
                                            <button
                                                className="delete-button"
                                                onClick={(e) => handleDelete(e, image.imageId)}
                                                title="Delete"
                                                disabled={fadingOutImageId === image.imageId}
                                            >
                                                <MdDelete />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Image Detail Modal */}
            {showDetailModal && (
                <div className="modal-overlay" onClick={handleCloseDetailModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        {!detailedImage && (
                            <button className="modal-close" onClick={handleCloseDetailModal}>
                                ✕
                            </button>
                        )}

                        {isLoadingDetail ? (
                            <div className="modal-loading">
                                <div className="spinner"></div>
                                <p>Loading image details...</p>
                            </div>
                        ) : detailedImage ? (
                            <>
                                <div className="modal-header">
                                    <h2>{detailedImage.title || 'Research Figure'}</h2>
                                    <div className="header-actions">
                                        {selectedImage && (
                                            <>
                                                <button
                                                    className={`download-button-modal ${isDownloading ? 'downloading' : ''}`}
                                                    onClick={() => handleDownloadFromUrl(selectedImage.imageId, detailedImage.title)}
                                                    disabled={isDownloading || isCopying}
                                                >
                                                    {isDownloading ? (
                                                        <>
                                                            <span className="spinner"></span>
                                                            Downloading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <AiOutlineDownload /> Download
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    className={`share-button-modal ${isCopying ? 'copying' : ''}`}
                                                    onClick={() => handleCopyToClipboard(selectedImage.imageId, detailedImage.title)}
                                                    disabled={isCopying || isDownloading}
                                                    title="Copy to clipboard"
                                                >
                                                    {isCopying ? (
                                                        <>
                                                            <span className="spinner"></span>
                                                            Copied!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MdShare /> Share
                                                        </>
                                                    )}
                                                </button>
                                            </>
                                        )}
                                        <button className="header-close-btn" onClick={handleCloseDetailModal}>
                                            ✕
                                        </button>
                                    </div>
                                </div>

                                {detailedImage.image_url && (
                                    <div className="modal-image-container">
                                        <img src={detailedImage.image_url} alt={detailedImage.title} />
                                    </div>
                                )}

                                <div className="modal-details">
                                    <div className="detail-section">
                                        <h3>Abstract</h3>
                                        <p>{detailedImage.abstract}</p>
                                    </div>

                                    <div className="detail-section">
                                        <h3>Metadata</h3>
                                        <div className="metadata-grid-modal">
                                            <div className="metadata-item-modal">
                                                <strong>Entities</strong>
                                                <span>{detailedImage.metadata?.total_entities || 0}</span>
                                            </div>
                                            <div className="metadata-item-modal">
                                                <strong>Relationships</strong>
                                                <span>{detailedImage.metadata?.total_relationships || 0}</span>
                                            </div>
                                            <div className="metadata-item-modal">
                                                <strong>Processing Time</strong>
                                                <span>{detailedImage.metadata?.processing_time_seconds?.toFixed(2) || 'N/A'}s</span>
                                            </div>
                                            <div className="metadata-item-modal">
                                                <strong>Created</strong>
                                                <span>{new Date(detailedImage.createdAt).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {detailedImage.entities && detailedImage.entities.length > 0 && (
                                        <div className="detail-section">
                                            <h3>Extracted Entities ({detailedImage.entities.length})</h3>
                                            <div className="entities-list">
                                                {detailedImage.entities.map((entity, index) => (
                                                    <div key={index} className="entity-item">
                                                        <span className="entity-text">{entity.text}</span>
                                                        <span className="entity-type">{entity.type}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {detailedImage.relationships && detailedImage.relationships.length > 0 && (
                                        <div className="detail-section">
                                            <h3>Relationships ({detailedImage.relationships.length})</h3>
                                            <div className="relationships-list">
                                                {detailedImage.relationships.map((rel, index) => (
                                                    <div key={index} className="relationship-item">
                                                        <span className="rel-source">{rel.source}</span>
                                                        <span className="rel-arrow">→</span>
                                                        <span className="rel-type">{rel.relation}</span>
                                                        <span className="rel-arrow">→</span>
                                                        <span className="rel-target">{rel.target}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="modal-error">
                                <p>Failed to load image details.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {
                showDeleteConfirm && (
                    <div className="modal-overlay delete-overlay" onClick={cancelDelete}>
                        <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="delete-header">
                                <h3>Delete Research Figure?</h3>
                            </div>
                            <div className="delete-body">
                                <p>Are you sure you want to delete this item? This action cannot be undone.</p>
                            </div>
                            <div className="delete-actions">
                                <button className="cancel-btn" onClick={cancelDelete} disabled={isDeleting}>Cancel</button>
                                <button
                                    className={`confirm-delete-btn ${isDeleting ? 'deleting' : ''}`}
                                    onClick={confirmDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? (
                                        <>
                                            <span className="spinner"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <MdDelete /> Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Payment Modal */}
            {showPaymentModal && (
                <PaymentModal
                    onClose={() => setShowPaymentModal(false)}
                    onSuccess={() => {
                        setShowPaymentModal(false);
                        fetchUsageInfo();
                    }}
                />
            )}
        </div >
    );
};

export default BrainSnap;
